import { Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import type { PurchaseOrderDetail } from '@/types'
import { printPurchaseOrder } from '@/lib/printPurchaseOrder'
import type { CustomerReceiptSettings } from '@/lib/printCustomerReceipt'
import { poRef } from './poStatus'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  detail: PurchaseOrderDetail | null
  businessName: string
  formatCurrency: (amount: number) => string
  receiptSettings: CustomerReceiptSettings
}

/** Optional thermal print of an order — on the shared Dialog (portals to
 *  body), replacing a hand-rolled z-[60] overlay that collided with the toast
 *  stack. Same flow as the KOT print confirm. */
export function PoPrintModal({ open, onOpenChange, detail, businessName, formatCurrency, receiptSettings }: Props) {
  const lineCount = detail?.lines?.length ?? 0

  const handlePrint = () => {
    if (!detail) return
    printPurchaseOrder(detail, { businessName, formatCurrency, cfg: receiptSettings })
    onOpenChange(false)
  }

  return (
    <Dialog open={open && !!detail} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">Print order{detail ? ` #${poRef(detail.id)}` : ''}?</DialogTitle>
          <DialogDescription>
            Opens a receipt-style <strong>80mm</strong> slip for your thermal printer — handy for staff
            shopping lists or a paper copy for the vendor. You can cancel in the system print dialog if
            you change your mind.
          </DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm space-y-1">
            <p>
              <span className="text-muted-foreground">Vendor:</span>{' '}
              <span className="font-medium">{detail.supplier_name}</span>
            </p>
            <p>
              <span className="text-muted-foreground">Items:</span>{' '}
              <span className="font-medium tabular-nums">{lineCount}</span>
            </p>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Skip printing
          </Button>
          <Button type="button" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4 shrink-0" aria-hidden />
            Print slip
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
